'use client'

import OfferMessage from './offer-message'

import OfferUsd from './offer-usd'
import Link from 'next/link'
import useSWR from 'swr'
import { useEffect, useState } from 'react'
import BitcoinIcon from '@/icons/bitcoin-filled'
import InstantSellButton from './instant-sell-button'
import InstantSell from './instant-sell'
import Loading from '@/components/circle-loading'
import { type Offer } from '@/utils/offers'
import { fetchItemOffers, formatOfferBtc, isActiveOffer, offerLabel } from '@/utils/offer-inbox'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import Wallet from '@/utils/wallet'
import OfferExpiry from './offer-expiry'
import CancelOfferModal from './cancel-offer-modal'
import styles from './offer-dialog.module.css'

export default function ItemOffersPanel({
	id,
	collectionSlug,
	collectionName,
	isOwner,
	onReview
}: {
	id: string
	collectionSlug?: string
	collectionName?: string | null
	isOwner: boolean
	onReview: (offer: Offer, action: 'accept' | 'decline') => void
}) {
	// Public market data: reading offers never requires a connected wallet.
	const { data, error, mutate, isLoading } = useSWR(
		['offers:item', id],
		([, id]) => fetchItemOffers(id),
		{ refreshInterval: 30_000, revalidateOnFocus: true }
	)
	const loggedIn = useLoggedIn()
	const wallet = loggedIn ? Wallet.getTaprootAddress() : null
	const [cancelling, setCancelling] = useState<Offer | null>(null)
	const [now, setNow] = useState(Date.now)
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [])
	const offers = [
		...new Map(
			[...(data?.offers || []), ...(data?.collection_offers || [])]
				.filter((o) => isActiveOffer(o, now))
				.map((o) => [o.id, o])
		).values()
	].sort((a, b) => b.price_sats - a.price_sats)
	const mine = offers.filter((o) => o.buyer_address === wallet)

	const renderOffer = (o: Offer) => (
<article key={o.id} className={styles.offerListRow}>
							<div className={styles.itemOfferDetails} data-top-offer={o.id === offers[0].id}>
								<strong>
									{formatOfferBtc(o.price_sats)} BTC <OfferUsd sats={o.price_sats} />{' '}
									{o.buyer_address === wallet && (
										<span className={styles.ownOfferBadge}>Your offer</span>
									)}
								</strong>
								<small>
									<span className={styles.itemOfferKind}>{offerLabel(o)}</span><span className={styles.itemOfferSeparator}> · </span>
									<Link href={`/address/${o.buyer_address}`}>
										From {o.buyer_address.slice(0, 6)}…{o.buyer_address.slice(-4)}
									</Link>
								</small>
								<OfferExpiry offer={o} now={now} countdown />
								<OfferMessage offer={o} message={o.message} />
							</div>
							{o.buyer_address === wallet && (
								<button
									type="button"
									className={styles.cancelOfferButton}
									onClick={() => setCancelling(o)}
								>
									Cancel offer
								</button>
							)}
							{isOwner && o.buyer_address !== wallet && (
								<div className={styles.itemOfferActions}>
									{o.id !== offers[0].id && <InstantSellButton onClick={() => onReview(o, 'accept')} />}
									{o.scope === 'item' && (
										<button type="button" onClick={() => onReview(o, 'decline')}>
											Decline
										</button>
									)}
								</div>
							)}
						</article>
	)
	return (
        <>
		<section id="item-offers" className={styles.itemOfferPanel} aria-label="Offers on this item">
			<header className={styles.publicOffersHeader}>
				<h3>
					Offers
					{offers.length > 0 && <span className={styles.offerCount}>{offers.length} active</span>}
				</h3>
			</header>
			{isLoading ? (
				<div role="status" aria-label="Loading offers" className="flex justify-center py-8"><Loading className="!w-6 !h-6" /></div>
			) : error ? (
				<p role="status">
					Offers could not be refreshed.{' '}
					<button type="button" onClick={() => void mutate()}>
						Retry
					</button>
				</p>
			) : !offers.length ? (
				<p>No active offers.</p>
			) : null}
            {offers.length > 0 && <>
                <div className={styles.topOfferActions}>
                    <div className={styles.publicOfferSummary}>
                        <span>Top offer</span>
                        <strong><BitcoinIcon className="size-3.5 shrink-0" aria-hidden="true" /> {formatOfferBtc(offers[0].price_sats)} BTC <OfferUsd sats={offers[0].price_sats} /></strong>
                    </div>
                    {isOwner && offers[0].buyer_address !== wallet ? <InstantSellButton className={styles.topOfferSell} onClick={() => onReview(offers[0], 'accept')} /> : collectionSlug && <InstantSell slug={collectionSlug} name={collectionName} compact hideUnavailable className={styles.topOfferSell} />}
                </div>
                {renderOffer(offers[0])}
            </>}

			{cancelling && (
				<CancelOfferModal
					offer={cancelling}
					onClose={() => setCancelling(null)}
					onChanged={() => void mutate()}
				/>
			)}
		</section>
        {mine.length > 0 && <section className={`${styles.itemOfferPanel} ${styles.myItemOffers}`} aria-label="My offers on this item">
            <header className={styles.publicOffersHeader}><h3>My offers <span className={styles.offerCount}>{mine.length} active</span></h3></header>
            {mine.map(renderOffer)}
        </section>}
        </>
	)
}
